import React, { useMemo, useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {
  FiGrid,
  FiMove,
  FiAlertTriangle,
  FiPrinter,
  FiCheckCircle,
  FiCalendar
} = FiIcons

const ScopeSequencePlanner = () => {
  const [quarters, setQuarters] = useState([
    {
      name: 'Quarter 1',
      weeks: [
        { id: 1, title: 'Number Sense & Place Value', subject: 'Math', coverage: 90 },
        { id: 2, title: 'Narrative Writing', subject: 'ELA', coverage: 70 },
        { id: 3, title: 'Habitats & Adaptations', subject: 'Science', coverage: 60 }
      ]
    },
    {
      name: 'Quarter 2',
      weeks: [
        { id: 4, title: 'Fractions & Decimals', subject: 'Math', coverage: 50 },
        { id: 5, title: 'Research & Presentations', subject: 'ELA', coverage: 40 },
        { id: 6, title: 'Forces & Motion', subject: 'Science', coverage: 35 }
      ]
    }
  ])
  const [selectedWeek, setSelectedWeek] = useState(1)

  const coverageGaps = useMemo(() => {
    const gaps = []
    quarters.forEach((quarter) =>
      quarter.weeks.forEach((week) => {
        if (week.coverage < 60) {
          gaps.push({ quarter: quarter.name, week: week.title, subject: week.subject })
        }
      })
    )
    return gaps
  }, [quarters])

  const moveWeek = (weekId, direction) => {
    setQuarters((prev) => {
      const flat = prev.flatMap((q) => q.weeks)
      const index = flat.findIndex((week) => week.id === weekId)
      const swapIndex = index + direction
      if (swapIndex < 0 || swapIndex >= flat.length) return prev
      const reordered = [...flat]
      const temp = reordered[index]
      reordered[index] = reordered[swapIndex]
      reordered[swapIndex] = temp

      // rebuild quarters
      const rebuilt = []
      let pointer = 0
      for (const quarter of prev) {
        const weekCount = quarter.weeks.length
        rebuilt.push({
          ...quarter,
          weeks: reordered.slice(pointer, pointer + weekCount)
        })
        pointer += weekCount
      }
      return rebuilt
    })
  }

  const handlePrint = () => {
    window.print()
  }

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-[#00AAFF] to-[#1B4C87] rounded-2xl p-8 text-white">
        <h1 className="text-3xl font-bold mb-3">Scope & Sequence Planner</h1>
        <p className="text-blue-50 max-w-3xl">
          Visualize quarter-by-quarter coverage, shift lessons across weeks, and surface coverage
          gaps instantly.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiGrid} className="w-5 h-5 text-[#00AAFF]" />
            <h2 className="text-xl font-bold text-gray-800">Quarterly Map</h2>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1B4C87] text-white hover:bg-[#163d6d]"
          >
            <SafeIcon icon={FiPrinter} className="w-4 h-4" />
            Print scope & sequence
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {quarters.map((quarter) => (
            <div key={quarter.name} className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-800">{quarter.name}</h3>
                <div className="flex items-center text-xs text-gray-600">
                  <SafeIcon icon={FiCalendar} className="w-4 h-4 mr-1" />
                  {quarter.weeks.length} weeks
                </div>
              </div>
              <div className="space-y-3">
                {quarter.weeks.map((week) => (
                  <div key={week.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-800">{week.title}</p>
                        <p className="text-sm text-gray-600">{week.subject}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          className="p-2 rounded-full bg-white border border-gray-200 hover:border-[#00AAFF]"
                          onClick={() => moveWeek(week.id, -1)}
                          aria-label="Move up"
                        >
                          <SafeIcon icon={FiMove} className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          className="p-2 rounded-full bg-white border border-gray-200 hover:border-[#00AAFF]"
                          onClick={() => moveWeek(week.id, 1)}
                          aria-label="Move down"
                        >
                          <SafeIcon icon={FiMove} className="w-4 h-4 rotate-180 text-gray-600" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-[#00AAFF] to-[#1B4C87]"
                        style={{ width: `${week.coverage}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Coverage: {week.coverage}%</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gaps */}
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-3">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-[#F97316]" />
            <h3 className="text-lg font-bold text-gray-800">Coverage Gaps</h3>
          </div>
          {coverageGaps.length === 0 ? (
            <p className="text-sm text-gray-600">No gaps detected. Great job!</p>
          ) : (
            <ul className="space-y-2 text-sm text-gray-700">
              {coverageGaps.map((gap, index) => (
                <li key={`${gap.week}-${index}`} className="p-2 bg-gray-50 rounded-lg">
                  {gap.quarter}: <span className="font-semibold">{gap.week}</span> ({gap.subject})
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Selected Week */}
        <div className="bg-white rounded-xl shadow-lg p-6 space-y-3 lg:col-span-2">
          <div className="flex items-center gap-2">
            <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-[#10B981]" />
            <h3 className="text-lg font-bold text-gray-800">Week Details</h3>
          </div>
          <select
            className="border border-gray-200 rounded-lg px-3 py-2"
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(Number(e.target.value))}
          >
            {quarters.flatMap((quarter) =>
              quarter.weeks.map((week) => (
                <option key={week.id} value={week.id}>
                  {quarter.name} - {week.title}
                </option>
              ))
            )}
          </select>
          <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
            <p className="text-sm text-gray-700">
              Drag-and-drop is simulated by move controls. Reorder weeks to adjust pacing across
              quarters and close skill gaps.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              Print-ready maps keep parents, teachers, and administrators aligned on coverage.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ScopeSequencePlanner
